import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useMemo } from 'react';
import { filterPreferencesAPI, FilterPreferences } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export type FilterEntityType = 'CASE' | 'INCIDENT' | 'PROBLEM' | 'CHANGE' | 'ALERT' | 'SERVICE_REQUEST' | 'TIME_ENTRY';

const CACHE_KEY_PREFIX = 'filter-prefs';
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

// Helper to get localStorage cache (now user-specific)
function getLocalStoragePrefs(userId: string | undefined, entityType: FilterEntityType): FilterPreferences | null {
  if (!userId) return null;
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}-${userId}-${entityType}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 24 hours
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
        return data;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

// Helper to set localStorage cache (now user-specific)
function setLocalStoragePrefs(userId: string | undefined, entityType: FilterEntityType, filters: FilterPreferences) {
  if (!userId) return;
  try {
    localStorage.setItem(
      `${CACHE_KEY_PREFIX}-${userId}-${entityType}`,
      JSON.stringify({ data: filters, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}

// Helper to remove localStorage cache (now user-specific)
function removeLocalStoragePrefs(userId: string | undefined, entityType: FilterEntityType) {
  if (!userId) return;
  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}-${userId}-${entityType}`);
  } catch {
    // Ignore storage errors
  }
}

export function useFilterPreferences(entityType: FilterEntityType) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['filter-preferences', userId, entityType], [userId, entityType]);

  // Fetch preferences from server
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await filterPreferencesAPI.get(entityType);
      return response.data.data.filters as FilterPreferences;
    },
    staleTime: STALE_TIME,
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: () => {
      // Use localStorage cache as placeholder
      return getLocalStoragePrefs(userId, entityType) || {};
    },
    enabled: !!userId, // Only fetch when user is logged in
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Update localStorage when server data arrives
  useEffect(() => {
    if (query.data && userId) {
      setLocalStoragePrefs(userId, entityType, query.data);
    }
  }, [query.data, entityType, userId]);

  // Save mutation with optimistic update
  const saveMutation = useMutation({
    mutationFn: (filters: FilterPreferences) => {
      return filterPreferencesAPI.update(entityType, filters);
    },
    onMutate: async (newFilters) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousFilters = queryClient.getQueryData<FilterPreferences>(queryKey);

      // Optimistically update both caches
      queryClient.setQueryData(queryKey, newFilters);
      setLocalStoragePrefs(userId, entityType, newFilters);

      return { previousFilters };
    },
    onError: (_err, _newFilters, context) => {
      // Rollback on error
      if (context?.previousFilters) {
        queryClient.setQueryData(queryKey, context.previousFilters);
        setLocalStoragePrefs(userId, entityType, context.previousFilters);
      }
    },
    onSettled: () => {
      // Always refetch after mutation settles
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: () => filterPreferencesAPI.reset(entityType),
    onSuccess: () => {
      queryClient.setQueryData(queryKey, {});
      removeLocalStoragePrefs(userId, entityType);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Wrapper that uses mutateAsync and waits for refetch
  const saveFilters = useCallback(async (filters: FilterPreferences) => {
    await saveMutation.mutateAsync(filters);
    // Force refetch to ensure we have the latest data
    await queryClient.refetchQueries({ queryKey });
  }, [saveMutation, queryClient, queryKey]);

  // Wrapper for reset that uses mutateAsync
  const resetFilters = useCallback(async () => {
    await resetMutation.mutateAsync();
    // Force refetch to ensure we have the latest data
    await queryClient.refetchQueries({ queryKey });
  }, [resetMutation, queryClient, queryKey]);

  return {
    filters: query.data || {},
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    error: query.error,
    saveFilters,
    resetFilters,
    isSaving: saveMutation.isPending,
    isResetting: resetMutation.isPending,
  };
}
